import { Button, Grid } from '@mui/material';
import { usePhrase, useVerificationInfo } from '@/hooks';
import { Header, HeaderSkeleton } from './Header/Header';
import { PendingReview } from './PendingReview/PendingReview';
import {
    RequestedInfoMessages,
    RequestedInfoMessagesSkeleton,
} from './RequestedInfoMessages/RequestedInfoMessages';
import { VerificationForms } from './VerificationForms/VerificationForms';
import { PersonalInfoFormSkeleton } from './VerificationForms/ProfessionalInfoForm';
import { DocumentsFormSkeleton } from './VerificationForms/DocumentsForm/DocumentsForm';
import { Query } from '@/components/layout/Query/Query';
import { isEmpty } from '@/utils/fp/isEmpty/isEmpty';
import { VERIFICATION_PHRASES } from '@/consts/phrases/verificationPhrases';
import { Link } from '@/components/general/Link/Link';
import { useAuthStore } from '@/store/authStore/authStore';

import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';

interface VerificationProps {
    requestToken: string;
}

const VerificationSkeleton = (props: { professionalInfoFormEnabled: boolean }) => (
    <Grid size={12}>
        <HeaderSkeleton />
        <RequestedInfoMessagesSkeleton />
        {props.professionalInfoFormEnabled && <PersonalInfoFormSkeleton />}
        <DocumentsFormSkeleton />
    </Grid>
);

export const Verification = (props: VerificationProps) => {
    const { data, isLoading, error } = useVerificationInfo(props.requestToken);
    const { t } = usePhrase();
    const isLoggedIn = useAuthStore((s) => s.loggedIn);

    const stage = data?.stage;

    const professionalInfoFormEnabled = isLoading || !isEmpty(data?.professionalInfo);

    const isAutoIdentityVerificationEnabled = data?.isAutoIdentityVerificationEnabled;
    const isManualIdentityVerificationEnabled = data?.isManualIdentityVerificationEnabled;
    const requestedInfo = data?.requestedInfo || [];

    const isOnlineIdentityVerificationOnly = Boolean(
        requestedInfo.some((info) => info === 'identification') &&
            isAutoIdentityVerificationEnabled &&
            !isManualIdentityVerificationEnabled,
    );

    const returnToAccountHref = isOnlineIdentityVerificationOnly
        ? '/account/personal-details'
        : '/account/professional-details';

    return (
        <Grid container>
            <Grid size={12} sx={{ mb: 2 }}>
                {isLoggedIn && (
                    <Link href={returnToAccountHref} passHref>
                        <Button variant="text" size="small" startIcon={<KeyboardBackspaceIcon />}>
                            {t(VERIFICATION_PHRASES.BACK_TO_ACCOUNT)}
                        </Button>
                    </Link>
                )}
            </Grid>
            <Query
                data={data}
                error={error}
                isLoading={isLoading}
                loaderComponent={
                    <VerificationSkeleton
                        professionalInfoFormEnabled={professionalInfoFormEnabled}
                    />
                }
            >
                {({ data }) => (
                    <>
                        {stage === 'pending-review' && <PendingReview />}
                        {!isOnlineIdentityVerificationOnly && <Header verificationInfo={data} />}
                        {stage !== 'pending-review' && (
                            <RequestedInfoMessages
                                requestToken={props.requestToken}
                                verificationInfo={data}
                            />
                        )}
                        <VerificationForms
                            requestToken={props.requestToken}
                            verificationInfo={data}
                            isOnlineIdentityVerificationOnly={isOnlineIdentityVerificationOnly}
                        />
                    </>
                )}
            </Query>
        </Grid>
    );
};
